import Image from '@atoms/Image/Image';
import MotionWrapper from '@atoms/MotionWrapper/MotionWrapper';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import type { IInteractiveFeatureDisplayTab } from './InteractiveFeatureDisplay';

interface Props extends IInteractiveFeatureDisplayTab {
  readonly imageHeights: string;
  readonly isScrollAnimationEnabled?: boolean;
  readonly isSectionVisible?: boolean;
  readonly imageContainerRefCallback: (element: HTMLDivElement | null) => void;
}

const InteractiveFeatureDisplayTab = ({
  text,
  tabName,
  mobileImage,
  image,
  meta,
  imageHeights,
  isScrollAnimationEnabled,
  isSectionVisible = true,
  imageContainerRefCallback,
}: Props) => {
  const isMobile = useMediaQuery(768);
  const isEditMode = meta?.editMode;

  return (
    <div className="flex w-full flex-col items-center gap-10 lg:gap-13">
      {isEditMode && (
        <h6
          className="pb-2.5 text-white"
          data-epi-property-name={meta?.editHints?.tabName}
        >
          {tabName}
        </h6>
      )}

      {text && (
        <MotionWrapper
          key={tabName}
          variants={{
            initial: {
              opacity: 0,
              y: isSectionVisible ? 0 : 60,
            },
            animate: {
              opacity: 1,
              y: 0,
            },
          }}
          transition={{ opacity: { duration: 0.5 }, y: { duration: 0.8 } }}
          initial="initial"
          animate={isSectionVisible ? 'animate' : 'initial'}
          className="h6 text-center text-grey-500 lg:w-3/5"
          dangerouslySetInnerHTML={{ __html: text }}
          data-testid="CT-171-Interactive-Feature-Display-Text"
          data-epi-property-name={meta?.editHints?.text}
        />
      )}

      <MotionWrapper
        layout="position"
        ref={imageContainerRefCallback}
        className={classNames(
          'relative w-full',
          imageHeights ||
            'min-h-[125px] md:min-h-[250px] lg:min-h-[300px] 2xl:min-h-[460px]'
        )}
        variants={{
          initial: {
            opacity: 0,
            transition: { duration: 0.3 },
          },
          animate: {
            opacity: 1,
            transition: {
              duration: 0.3,
              delay: isScrollAnimationEnabled ? 0.6 : 0,
            },
          },
        }}
        initial="initial"
        animate={isSectionVisible ? 'animate' : 'initial'}
      >
        <AnimatePresence mode="popLayout">
          <MotionWrapper
            key={tabName}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 flex w-full items-center justify-center"
          >
            <Image
              image={isMobile ? mobileImage : image}
              dataTestId="CT-171-Interactive-Feature-Display-Image"
              classes="h-full w-full"
              imageClasses="h-full w-full object-contain"
              epiEdit={meta?.editHints?.image as string}
            />

            {isEditMode && (
              <Image
                image={mobileImage}
                dataTestId="CT-171-Interactive-Feature-Display-Image"
                classes="h-full w-full"
                imageClasses="h-full w-full object-contain"
                epiEdit={meta?.editHints?.mobileImage as string}
              />
            )}
          </MotionWrapper>
        </AnimatePresence>
      </MotionWrapper>
    </div>
  );
};

export default InteractiveFeatureDisplayTab;
